// eslint-disable-next-line simple-import-sort/imports
import '../styles/math-fonts.css';
import '../init';
import '@zep-us/zus/styles';
import '../styles/globals.scss';
import '@zep-us/cs/styles';
import { AnalyticsInitializer, GlobalErrorBoundary, QuizAppLoggerInitializer } from '@/components';
import { logger } from '@zep/core-logger';
import { crmUserContext } from '@zep-us/cs/user-context';
import { createHelpCenterUserInfo } from '@zep-us/cs/user-info';
import { HydrationBoundary, QueryCache, QueryClient, QueryClientProvider, QueryErrorResetBoundary } from '@tanstack/react-query';
import { type AppProps } from 'next/app';
import { CatchUnhandledRejectionErrors } from '@/components/CatchUnhandledRejectionErrors';
import { CountryCodeProvider } from '@/shared/contexts';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { ForcedSchoolGradeModalGate } from '@/shared/features/school-grade-required-modal';
import { usePointReceiveEvents } from '@/shared/features/classroom/hooks';
import { useIsInLti } from '@/shared/lti';
import { isHelpCenterDisabledPathname } from '@/shared/utils/helpCenterVisibility';
import { GlobalDialogManager } from '@/ui_v3';
import { GoogleAdSense } from '@/components/google-adsense';
import { Meta } from '../components/Meta';
import { type NextPageWithLayout } from 'next';
import { type UserInfo } from '@/types';
import NiceModal from '@ebay/nice-modal-react';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { OverlayProvider } from 'overlay-kit';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ScrollAssistButton } from '@/shared/ScrollAssistButton';
import { StorageInspectorContainer } from '@/module/etc/StorageInspector/StorageInspectorContainer';
import { isQaToolboxEnvEnabled } from '@/shared/features/qa-toolbox/utils';
import { SubscribeUserPlanContainer } from '@/module/plan';
import { Toaster } from 'react-hot-toast';
import { ZusToaster } from '@zep/toast';
import { UserPlanManagerProvider } from '@/shared/plan';
import { UserProvider, useUser } from '@/lib/auth';
import { appWithTranslation } from 'next-i18next';
import nextI18NextConfig from '../../next-i18next.config.js';
import { getRootLayout } from '@/layout';
import { isIgnoreRetryError } from '@/api/apiUtil';
import { isLocal, isProduction } from '@/consts/env';
import { useApiError } from '../hooks/useApiError';
import { createMutationCache } from './createMutationCache';
import { useIsMounted } from '@toss/react';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
const HelpCenterWidget = dynamic(() => import('@zep-us/cs').then(mod => mod.HelpCenterWidget), {
  ssr: false
});
const DEFAULT_DEV_HELPCENTER_CDN_URL = 'https://cdn-static-dev-1-crm.zep.us';

// design mode 전용 인스펙터(7964줄). 정적 import 하면 @zep/shared 가
// sideEffects 미설정이라 tree-shaking 이 안 돼 PRD 번들에 그대로 포함된다.
// next/dynamic + ssr:false 로 별도 청크 분리 → PRD 초기 번들에서 제외.
const DesignInspector = dynamic(() => import('@zep/internal').then(m => ({
  default: m.DesignInspector
})), {
  ssr: false
});

// @zep/crm SDK(core+react+ui, ~12.6KB gzip)는 팝업/웹푸시용이라 첫 페인트에 필요
// 없다. 정적 import 하면 전 유저의 _app 초기 번들에 상시 포함되므로, 앱 트리를 감싸지
// 않는 sibling 마운트 + ssr:false 로 별도 청크 분리해 critical 경로에서 뺀다.
const CrmWithAuth = dynamic(() => import('@/shared/features/crm/components').then(m => m.CrmWithAuth), {
  ssr: false
});
const QaToolboxRootLazy = dynamic(() => import('@/shared/features/qa-toolbox/components').then(m => ({
  default: m.QaToolboxRoot
})), {
  ssr: false
});

// 실제 prod 서비스 호스트(quiz.zep.us 등)에서는 QA 툴박스 청크조차 받지 않도록 게이트한다.
// Vercel Preview 는 STAGE=PRD 로 빌드되지만 QA 검증 환경이라, 빌드타임 env 로는 구분이
// 안 된다. 런타임 호스트명으로 판별해야 하므로 mount 이후(useEffect)에 결정한다.
const DevQaToolboxRoot = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    setEnabled(isQaToolboxEnvEnabled());
  }, []);
  return enabled ? <QaToolboxRootLazy data-sentry-element="QaToolboxRootLazy" data-sentry-component="DevQaToolboxRoot" data-sentry-source-file="_app.page.tsx" /> : null;
};
const PointReceiveEventBridge = () => {
  usePointReceiveEvents();
  return null;
};
const TOAST_STACK_GAP = 8;
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
function CustomApp({
  Component,
  pageProps
}: AppPropsWithLayout) {
  const handleError = useApiError();
  const isInExternalPlatform = useIsInExternalPlatform();
  const isInLti = useIsInLti();
  const {
    pathname
  } = useRouter();
  const [isClientReady, setClientReady] = useState(false);
  const shouldHideHelpCenterWidget = !isClientReady || Component.hideChannelTalk || isHelpCenterDisabledPathname(pathname) || isInLti || isInExternalPlatform;
  // 마운트(SSR/hydration) 완료 전에는 외부 플랫폼 판정이 불가하므로 보수적으로 disabled.
  // 마운트 후에는 useIsInExternalPlatform 이 진입 URL(첫 프레임)과 persist 된 컨텍스트를
  // 모두 반영하므로, GC/LTI 진입 시 CRM 식별/캠페인이 한 프레임도 새지 않는다(정책).
  const isMounted = useIsMounted();
  const isCrmDisabled = !isMounted || isInExternalPlatform;
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      mutations: {
        throwOnError: false
      },
      queries: {
        throwOnError: true,
        retry: (failureCount, error) => {
          if (isIgnoreRetryError(error)) return false;
          return failureCount <= 3;
        },
        retryDelay: attemptIndex =>
        // 재시도 간격을 1배씩 증가시키며 최대 30초까지
        Math.min(1000 * 2 * attemptIndex, 30000)
      }
    },
    mutationCache: createMutationCache(handleError),
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.ignoreError) {
          return;
        }
        handleError(error);
      }
    })
  }));
  const getLayout = Component.getLayout ?? getRootLayout;
  useEffect(() => {
    setClientReady(true);
  }, []);
  return <>
      {process.env.NEXT_PUBLIC_STAGE !== 'PRD' && <DesignInspector />}
      <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-source-file="_app.page.tsx">
        <QueryErrorResetBoundary data-sentry-element="QueryErrorResetBoundary" data-sentry-source-file="_app.page.tsx">
          <GlobalErrorBoundary data-sentry-element="GlobalErrorBoundary" data-sentry-source-file="_app.page.tsx">
            <UserProvider data-sentry-element="UserProvider" data-sentry-source-file="_app.page.tsx">
              <CountryCodeProvider data-sentry-element="CountryCodeProvider" data-sentry-source-file="_app.page.tsx">
                <QuizAppLoggerInitializer data-sentry-element="QuizAppLoggerInitializer" data-sentry-source-file="_app.page.tsx" />
                <AnalyticsInitializer data-sentry-element="AnalyticsInitializer" data-sentry-source-file="_app.page.tsx" />
                <UserPlanManagerProvider data-sentry-element="UserPlanManagerProvider" data-sentry-source-file="_app.page.tsx">
                  <OverlayProvider data-sentry-element="OverlayProvider" data-sentry-source-file="_app.page.tsx">
                    <GlobalDialogManager data-sentry-element="GlobalDialogManager" data-sentry-source-file="_app.page.tsx" />
                    <ForcedSchoolGradeModalGate data-sentry-element="ForcedSchoolGradeModalGate" data-sentry-source-file="_app.page.tsx" />
                    <CatchUnhandledRejectionErrors data-sentry-element="CatchUnhandledRejectionErrors" data-sentry-source-file="_app.page.tsx" />
                    <NuqsAdapter data-sentry-element="NuqsAdapter" data-sentry-source-file="_app.page.tsx">
                      <NiceModal.Provider data-sentry-element="unknown" data-sentry-source-file="_app.page.tsx">
                        <HydrationBoundary state={pageProps.dehydratedState} data-sentry-element="HydrationBoundary" data-sentry-source-file="_app.page.tsx">
                          <Meta pageProps={pageProps} data-sentry-element="Meta" data-sentry-source-file="_app.page.tsx" />
                          <SubscribeUserPlanContainer data-sentry-element="SubscribeUserPlanContainer" data-sentry-source-file="_app.page.tsx" />
                          <PointReceiveEventBridge data-sentry-element="PointReceiveEventBridge" data-sentry-source-file="_app.page.tsx" />
                          <div id="root">
                            {getLayout(<GlobalErrorBoundary>
                                <Component {...pageProps} />
                              </GlobalErrorBoundary>)}
                            <ScrollAssistButton data-sentry-element="ScrollAssistButton" data-sentry-source-file="_app.page.tsx" />
                          </div>
                          <Toaster position="top-right" gutter={TOAST_STACK_GAP} data-sentry-element="Toaster" data-sentry-source-file="_app.page.tsx" />
                          {/*
                            ZUS 디자인 시스템 토스트(zusToast). 앱 전체에 정확히 한 번만
                            마운트한다. 기존 react-hot-toast 는 그대로 두고, 신규 화면부터
                            점진적으로 이쪽으로 옮긴다.
                           */}
                          <ZusToaster data-sentry-element="ZusToaster" data-sentry-source-file="_app.page.tsx" />
                          <ReactQueryDevtools buttonPosition={'bottom-left'} initialIsOpen={false} data-sentry-element="ReactQueryDevtools" data-sentry-source-file="_app.page.tsx" />
                          <StorageInspectorContainer data-sentry-element="StorageInspectorContainer" data-sentry-source-file="_app.page.tsx" />
                          <DevQaToolboxRoot data-sentry-element="DevQaToolboxRoot" data-sentry-source-file="_app.page.tsx" />
                          {!shouldHideHelpCenterWidget && <HelpCenterWidgetQuiz />}
                        </HydrationBoundary>
                      </NiceModal.Provider>
                    </NuqsAdapter>
                    {/* CRM 은 앱 트리를 감싸지 않는 sibling 마운트다 — 앱 children 은
                        CRM context 를 소비하지 않는다. 팝업 위치도 배치에 무관하다:
                        모달은 overlay-kit portal, 캐러셀은 position:fixed(뷰포트 기준)라
                        DOM 위치와 독립. dynamic(ssr:false)로 지연 로드돼 초기 번들에서 빠진다. */}
                    <CrmWithAuth disabled={isCrmDisabled} data-sentry-element="CrmWithAuth" data-sentry-source-file="_app.page.tsx" />
                  </OverlayProvider>
                </UserPlanManagerProvider>
              </CountryCodeProvider>
            </UserProvider>
          </GlobalErrorBoundary>
        </QueryErrorResetBoundary>
      </QueryClientProvider>
      {isProduction && <GoogleAdSense />}
    </>;
}
function HelpCenterWidgetQuiz() {
  const {
    user
  } = useUser();
  const apiBaseUrl = resolveHelpCenterApiBaseUrl();
  const cdnBaseUrl = resolveHelpCenterCdnBaseUrl();
  const hasMissingConfig = !apiBaseUrl || !cdnBaseUrl;
  useEffect(() => {
    if (!isProduction || !hasMissingConfig) return;
    logger.captureError(new Error('helpcenter_config_missing'), {
      attributes: {
        source: 'HelpCenterWidget',
        app: 'quiz-app',
        missingApiBaseUrl: String(!apiBaseUrl),
        missingCdnBaseUrl: String(!cdnBaseUrl),
        stage: process.env.NEXT_PUBLIC_STAGE ?? ''
      }
    });
  }, [apiBaseUrl, cdnBaseUrl, hasMissingConfig]);
  if (hasMissingConfig) return null;
  return <HelpCenterWidget apiBaseUrl={apiBaseUrl} cdnBaseUrl={cdnBaseUrl} appId="helpdesk-web" service="quiz"
  // CRM has a dedicated inquiry workflow route keyed by this virtual path.
  urlPath="/inquiry" showSearch={false} showHelpCategories={false} userInfo={toQuizHelpCenterUserInfo(user)} onError={error => logger.captureError(error, {
    attributes: {
      source: 'HelpCenterWidget'
    }
  })} data-sentry-element="HelpCenterWidget" data-sentry-component="HelpCenterWidgetQuiz" data-sentry-source-file="_app.page.tsx" />;
}
function toQuizHelpCenterUserInfo(user: UserInfo | null | undefined) {
  return createHelpCenterUserInfo(user, user => ({
    email: user.email,
    name: user.username,
    context: {
      ...crmUserContext({
        country_code: user.countryCode,
        user_type: user.type,
        plan_type: user.planType,
        is_paid_user: user.planType !== 'FREE'
      }),
      plan_status: user.planStatus,
      plan_started_at: user.planDuration?.startDate,
      plan_expires_at: user.planDuration?.endDate,
      quiz_user_id: user.quizUserId,
      is_ambassador: user.isAmbassador,
      is_admin: user.admin,
      workspace_id: user.workspace?.workspaceHashId,
      workspace_name: user.workspace?.workspaceName,
      is_paid_workspace_owner: user.workspace?.isPaidOwner,
      teacher_school_type_code: user.teacherSchoolInfo?.schoolTypeCode,
      teacher_school_grade_code: user.teacherSchoolInfo?.schoolGradeCode,
      recent_quiz_created_at: user.recentQuizsetInfo?.createdAt,
      recent_quiz_school_type: user.recentQuizsetInfo?.schoolType,
      recent_quiz_school_grade: user.recentQuizsetInfo?.schoolGrade,
      recent_quiz_school_semester: user.recentQuizsetInfo?.schoolSemester
    }
  }));
}
function resolveHelpCenterApiBaseUrl() {
  if (isLocal) return '/crm';
  return process.env.NEXT_PUBLIC_CRM_URL ?? null;
}
function resolveHelpCenterCdnBaseUrl() {
  if (process.env.NEXT_PUBLIC_HELPCENTER_CDN_URL) {
    return process.env.NEXT_PUBLIC_HELPCENTER_CDN_URL;
  }
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') {
    return DEFAULT_DEV_HELPCENTER_CDN_URL;
  }
  return null;
}

// 2번째 인자(configOverride)를 넘겨 pageProps._nextI18Next 가 없는 렌더에서도
// appWithTranslation 이 null 을 반환(=I18nextProvider 미마운트)하지 않게 한다.
// 누적된 i18next 싱글톤 스토어를 그대로 사용해 뒤로가기 시 번역키 노출(common:name)을 막는다.
export default appWithTranslation(CustomApp, nextI18NextConfig);